import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import { MarketingCampaigns } from '@prisma/client';
import { PocketflowsCampaign } from '../../types/marketing';
import { finalizeCampaign } from '../../services/marketing';
import useGetMarketingData from './useGetMarketingData';
import useUser from '../global/useUser';

interface Payload {
  campaign: MarketingCampaigns;
}

interface Options {
  onSuccess?: (campaign: PocketflowsCampaign) => void;
  onError?: (message?: string) => void;
  onMutate?: (variables: Payload) => void;
  onSettled?: () => void;
}

function useFinalizeCampaign({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user } = useUser();
  const { data, isFetching } = useGetMarketingData();
  return useMutation({
    mutationFn: async ({ campaign }: Payload) => {
      if (!data?.id || isFetching || !user?.business?.id) return null;
      const result = await finalizeCampaign({
        campaignId: campaign.id,
        businessId: user.business.id,
        marketingBusinessId: data.id,
      });
      return result;
    },
    onSuccess: async result => {
      if (!result || 'error' in result)
        throw new Error(result?.error || 'Failed to create campaign');
      if (onSuccess) onSuccess(result.sourceCampaign);
    },
    onError: (err: any) => {
      if ('message' in err) {
        if (err.message === 'No customers found') {
          onError?.('No customers were found for the selected segments');
          return;
        }
        if (err.message === 'No payment method found') {
          onError?.(
            'You do not have a payment method set up. Visit the subscription page to add a payment method.',
          );
          return;
        }
      }
      if (err instanceof ValidationError) {
        onError?.(err.message);
        return;
      }
      onError?.('Failed to create campaign');
    },
    onMutate,
    onSettled,
  });
}

export default useFinalizeCampaign;
